// Models
import { AssistantConfig } from '@shared/components/ignite-assistant/ignite-assistant.config';

/**
 * The assistant as it appears on the API reference: an API-documentation agent
 * reached at `POST /chat/docs`, answering questions about the OpenAPI document
 * this reader is permitted to see and generating code samples for it.
 *
 * History and feedback stay on. Docs turns persist to the same `chat_history`
 * table as the IGP assistant's; the `docs:` thread prefix (applied here
 * and enforced again server-side) is what keeps the two histories apart.
 *
 * Every suggested question is deliberately resource-agnostic. docs-portal-api
 * builds the document from the caller's own scopes, so which operations exist
 * differs per reader — naming one here ("How do I list exports?") would offer
 * a question the assistant must decline for anyone without that scope. These
 * ask about the API's shape and conventions instead, which hold whatever the
 * reader can see.
 */
export const DOCS_ASSISTANT_CONFIG: AssistantConfig = {
  title: 'API Assistant',
  greeting: {
    role: 'bot',
    text: 'Hi. I can explain any endpoint in this API reference and write you a working code sample in curl, JavaScript, Python, Java, C#, Go, Ruby, or PHP. What are you trying to call?',
  },
  presetQuestions: ['How do I authenticate?', 'Which endpoints can I call?', 'How does pagination work?', 'Show me a curl example'],
  helpCategories: [
    {
      category: 'Getting started',
      description: 'Authentication, hosts, and conventions',
      questions: ['How do I authenticate a request?', 'Which host should I send requests to?', 'How are these APIs versioned?', 'How do I target a specific client?', 'What does a bearer token look like in a request?'],
    },
    {
      category: 'Finding an endpoint',
      description: 'Locate the operation you need',
      questions: ['Which endpoints can I call?', 'What can this API do?', 'Show me the endpoints grouped by tag', 'Which endpoints read data, and which change it?', 'How do I find the right endpoint for what I need?'],
    },
    {
      category: 'Request and response detail',
      description: 'Parameters, bodies, and schemas',
      questions: [
        'What parameters does that endpoint accept?',
        'Which parameters are required?',
        'What does the request body look like?',
        'Which fields come back in the response?',
        'Which status codes can that endpoint return?',
        'How does pagination work?',
      ],
    },
    {
      category: 'Code samples',
      description: 'Working examples in your language',
      questions: ['Show me a curl example for that endpoint', 'Write that in Python using requests', 'Give me the JavaScript fetch version', 'Show me how to call it from Java', 'Show me the Go version'],
    },
  ],
  inputPlaceholder: 'Ask about this API…',
  waitingPlaceholder: 'Please wait while I check the documentation…',
  errorMessage: 'Sorry — I couldn’t reach the API assistant just now. Please try again in a moment.',
  chatEndpointKey: 'chatDocs',
  product: 'igp',
  assistant: 'docs',
  features: { history: true, feedback: true, help: true },
};
